import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { utils } from '@app/shared';
import { AiRunnerConfig, AiRunnerDialogComponent } from 'src/app/features/ai/components/ai-runner';
import { AiRun, AiTicketPipeline } from 'src/app/features/ai/core/models/ai.model';
import { AiRunService, AiTicketService } from 'src/app/features/ai/core/services/ai-api.service';
import { canExecuteAi, canReadAi } from 'src/app/features/ai/core/utils/ai-access.util';
import { buildTicketAnalysisConfig } from '../../core/ai/ticket-analysis.config';
import { TicketDetail } from '../../core/models/ticket.model';
import { TicketService } from '../../core/services/ticket.service';

const DECIDED_KEY = 'v3ndor-ticket-ai-decided';
const DECIDED_LIMIT = 200;
/** Resultado concluído que ainda espera decisão só volta ao reabrir por este tempo. */
const RESUME_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Botão "Análise por IA" + `ai-runner-dialog` com a config de chamado. As pipelines vêm do servidor
 * (as que o usuário pode executar neste chamado); ao abrir, só o que ele ainda não viu volta sozinho.
 */
@Component({
  selector: 'app-ticket-ai-analysis',
  standalone: true,
  imports: [ButtonModule, TooltipModule, AiRunnerDialogComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // O host não vira caixa de layout: o botão continua sendo filho direto do cabeçalho.
  styles: [':host { display: contents; }'],
  template: `
    @if (canOpen) {
      <p-button
        icon="pi pi-sparkles"
        [rounded]="true"
        [text]="true"
        severity="secondary"
        [pTooltip]="'Análise por IA'"
        tooltipPosition="bottom"
        aria-label="Análise por IA"
        [disabled]="!ticket() || opening()"
        [loading]="opening()"
        (onClick)="open()"
        [styleClass]="buttonClass()">
      </p-button>
      @if (config(); as cfg) {
        <ai-runner-dialog
          [visible]="visible()"
          [config]="cfg"
          (visibleChange)="visible.set($event)"
          (actionsApplied)="markDecided($event.run.id)" />
      }
    }
  `,
})
export class TicketAiAnalysisComponent {
  private readonly ticketService = inject(TicketService);
  private readonly aiTickets = inject(AiTicketService);
  private readonly aiRuns = inject(AiRunService);
  private readonly messages = inject(MessageService, { optional: true });

  /** Sem `can_read` em "Pipelines de IA" (nem para super admin) o botão (e o modal) nem existem. */
  readonly canOpen = canReadAi(true);
  /** Executar exige `can_execute` no módulo; por pipeline, o servidor decide (lista de disponíveis). */
  private readonly canExecuteModule = canExecuteAi(true);

  readonly ticket = input<TicketDetail | null>(null);
  readonly buttonClass = input('p-0 w-2rem h-2rem');
  /** Uma gravação da IA terminou: recarregue o chamado. */
  readonly saved = output<void>();
  /** Execução a abrir sozinha (link da notificação de fim de execução): abre o modal com o resultado. */
  readonly openRunId = input<string | null>(null);
  /** O link de `openRunId` já foi usado: a tela pode limpar o parâmetro da URL. */
  readonly openedFromLink = output<void>();
  private linkHandled = false;

  readonly visible = signal(false);
  readonly opening = signal(false);
  readonly config = signal<AiRunnerConfig | null>(null);

  constructor() {
    effect(() => {
      const runId = this.openRunId();
      if (!runId || !this.ticket() || this.linkHandled || !this.canOpen) return;
      this.linkHandled = true;
      untracked(() => this.open(runId).then(() => this.openedFromLink.emit()));
    });
  }

  async open(runId?: string): Promise<void> {
    const ticket = this.ticket();
    if (!this.canOpen || !ticket || this.opening()) return;

    this.opening.set(true);
    try {
      let pipelines: AiTicketPipeline[] = [];
      if (this.canExecuteModule) {
        // Falha ao listar não impede ver resultados: o modal abre sem opções de execução, mas avisa.
        pipelines = await this.aiTickets.availablePipelines(ticket.id).catch(() => {
          this.messages?.add({
            severity: 'warn',
            summary: 'Análises indisponíveis',
            detail: 'Não foi possível listar as análises deste chamado. Os resultados continuam disponíveis.',
          });
          return [];
        });
      }
      const canRun = pipelines.length > 0;

      this.config.set(
        buildTicketAnalysisConfig(
          {
            ticketService: this.ticketService,
            ticket: () => this.ticket(),
            onSaved: () => this.saved.emit(),
          },
          {
            pipelines,
            canRun,
            resume: () =>
              runId ? this.aiRuns.get(runId) : this.findRunToResume(ticket.id, canRun),
            history: () => this.aiTickets.runs(ticket.id),
            markDecided: id => this.markDecided(id),
          }
        )
      );
      this.visible.set(true);
    } finally {
      this.opening.set(false);
    }
  }

  // Reabre sozinho só o não visto: em andamento (reconecta) ou concluído do próprio usuário.
  private async findRunToResume(ticketId: string, canRun: boolean): Promise<AiRun | null> {
    const runs = await this.aiTickets.runs(ticketId);

    const active = runs.find(run => run.status === 'pending' || run.status === 'running');
    if (active) return active;

    const me = String(utils.getUser()?.id ?? '').toLowerCase();
    const seen = this.readDecided();
    const limit = Date.now() - RESUME_WINDOW_MS;
    const unseen =
      runs.find(
        run =>
          run.status === 'completed' &&
          !run.published_at &&
          !seen.includes(run.id) &&
          (!canRun || String(run.requested_by ?? '').toLowerCase() === me) &&
          new Date(run.finished_at ?? run.created_at ?? 0).getTime() >= limit
      ) ?? null;

    // Abrir já conta como visto: ao fechar e reabrir, só o histórico.
    if (unseen) this.markDecided(unseen.id);
    return unseen;
  }

  markDecided(runId: string): void {
    try {
      const list = [...this.readDecided().filter(id => id !== runId), runId].slice(-DECIDED_LIMIT);
      localStorage.setItem(DECIDED_KEY, JSON.stringify(list));
    } catch {
      // Sem storage (modo privado): a decisão só não fica lembrada.
    }
  }

  private readDecided(): string[] {
    try {
      const parsed = JSON.parse(localStorage.getItem(DECIDED_KEY) ?? '[]');
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }
}
